import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type UnitKey } from '@fewerbags/core';
import type { IngredientSummary } from '@fewerbags/schema';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { UNIT_OPTIONS } from '../../lib/units';
import { groceryKeys } from '../../pages/groceries/queries';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { Field } from '../Field';
import { SelectField } from '../SelectField';
import { IngredientAutocomplete } from '../ingredients/IngredientAutocomplete';

/** Add a manual item to a grocery list: an ingredient (picked or free-typed), quantity, unit, note. */
export function AddGroceryItemDialog({
  open,
  onClose,
  listId,
}: {
  open: boolean;
  onClose: () => void;
  listId: string;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [selected, setSelected] = useState<IngredientSummary | null>(null);
  const [qty, setQty] = useState('1');
  const [unit, setUnit] = useState<UnitKey>('count');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: (input: Parameters<typeof api.groceries.items.add>[1]) =>
      api.groceries.items.add(listId, input),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: groceryKeys.detail(listId) });
      setText('');
      setSelected(null);
      setQty('1');
      setNote('');
      onClose();
    },
  });

  function onSubmit() {
    setFormError(null);
    const name = text.trim();
    if (!selected && !name) {
      setFormError('Choose an ingredient, or type its name.');
      return;
    }
    const qtyNum = Number(qty);
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
      setFormError('Enter a quantity greater than 0.');
      return;
    }
    add.mutate({
      ...(selected ? { ingredientId: selected.id } : { name }),
      qty: qtyNum,
      unit,
      note: note.trim() ? note.trim() : null,
    });
  }

  return (
    <Dialog open={open} onClose={onClose} title="Add an item">
      <div className="flex flex-col gap-3">
        <IngredientAutocomplete
          id="grocery-add-ingredient"
          label="Ingredient"
          value={text}
          onChange={(v) => {
            setText(v);
            setSelected(null);
          }}
          onSelect={(ing) => {
            setSelected(ing);
            setText(ing.canonicalName);
            setUnit(ing.defaultUnit);
          }}
          placeholder="Start typing…"
          helpText="Pick a suggestion, or type a new ingredient name."
        />
        <div className="grid grid-cols-2 gap-3">
          <Field
            id="grocery-add-qty"
            label="Quantity"
            type="number"
            inputMode="decimal"
            step="any"
            min="0"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
          <SelectField
            id="grocery-add-unit"
            label="Unit"
            options={UNIT_OPTIONS}
            value={unit}
            onChange={(e) => setUnit(e.target.value as UnitKey)}
          />
        </div>
        <Field
          id="grocery-add-note"
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        {formError ? <Alert tone="error">{formError}</Alert> : null}
        {add.isError ? <Alert tone="error">{messageForError(add.error)}</Alert> : null}
        <div className="flex gap-2">
          <Button onClick={onSubmit} disabled={add.isPending}>
            {add.isPending ? 'Adding…' : 'Add item'}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={add.isPending}>
            Cancel
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
